import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomService } from '@app/custom/services/custom.service';
import { SaleType } from '@app/shared/models/sale-type';
import { WizardService } from '@app/wizard-bar/services/wizard.service';
import { UserRole } from '@auth/models/utility-hub-identity';
import { forkJoin, from, Observable, of, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { CommodityType } from '@app/shared/enums/commodity-type';
import LogRocket from 'logrocket';
import { Address } from '@app/shared/models/address';
import { CustomerType } from '@app/shared/models/customer-type';
import { ProductTypeConfiguration } from '@configuration/models/product-type-configuration';
import { removeDuplicatePrimitives } from '@app/shared/helpers/array-helper';
import { catchError, distinctUntilChanged, switchMap, take, tap } from 'rxjs/operators';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { SalesType } from '@app/shared/enums/sales-type';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { UtilityHubService } from '@app/shared/services/utilityhub.service';
import { loggedInUserSelector, loggedInUtilityHubIdentitySelector, configurationSelector } from '@app/state/app.selectors';
import {
  SetQuoteRequestType,
  SetCustomerType,
  SetPageName,
  SetSalesType,
  SetSweetSpotCheckBoxState,
  SupplierUtilityInformation,
  SetCommodityType,
  SetProductType,
  SetUtilityUOM,
  ClearUtility,
  ClearUtilityInformation,
  SetUtilityInformation,
  SetUtility,
  SetBrokerageName,
  SetContractLengths,
} from '@utility-information/state/actions/utility-information.actions';
import {
  isDynamicSelector,
  brokerageNameSelector,
  zonesSelector,
  commodityTypeSelector,
  isCustomJourneyElectricEnabledSelector,
  isCustomJourneyGasEnabledSelector,
  isCustomJourneyEnabledSelector,
  quoteRequestStatusSelector,
  productTypesFromConfigSelector,
} from '@utility-information/state/selectors/utility-information.selectors';
import { Logger } from '@app/logging.service';
import { RenewalsCustomerDetailsData } from '../../models/renewals-customer-details-data.model';
import { RenewalsCustomConstants } from '../../models/renewals-custom-constants';
import { originalQuoteRequestIdSelector } from '@app/renewals/state/selectors/renewals-module.selectors';
import { SetOriginalQuoteRequestId } from '@app/renewals/state/actions/renewals-module.actions';

@Component({
  selector: 'app-renewals-custom-customer-details',
  templateUrl: './renewals-custom-customer-details.component.html',
  styleUrls: ['./renewals-custom-customer-details.component.scss'],
})
export class RenewalsCustomCustomerDetailsComponent implements OnInit, OnDestroy {
  constructor(
    private quoteService: CustomService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private wizardService: WizardService,
    private store: Store<AppState>,
    private quoteRequestService: QuoteRequestService,
    private pricingSourceService: PricingSourceService,
    private utilityHubService: UtilityHubService
  ) {
    this.renewalsCustomerDetailsData = new RenewalsCustomerDetailsData();
    this.logger = Logger.for(this);
  }

  private logger: Logger;

  frmCompanyDetails: FormGroup;
  renewalsCustomerDetailsData: RenewalsCustomerDetailsData;
  contractLengthInputsSource = new Subject<number[]>();

  isDynamic$: Observable<boolean>;
  brokerageName$: Observable<string>;
  zones$: Observable<string[]>;
  state$: Observable<string>;
  productTypes$: Observable<ProductTypeConfiguration[]>;
  contractLengthsFromProductType$: Observable<number[]>;

  utilities$: Subscription;
  saveCustomCompanyDetails$: Subscription;
  saveCompanyDetails$: Subscription;
  quoteRequestStatus$: Subscription;
  routeSubscription$: Subscription;
  selectLoggedInUserSubscription$: Subscription;
  selectLoggedInUtilityHubIdentitySubscription$: Subscription;
  loadSubscription$: Subscription;
  originalQuoteRequestIdSelector$: Subscription;

  get brokerageCtrl() {
    return this.frmCompanyDetails?.get('brokerage');
  }

  get brokerCtrl() {
    return this.frmCompanyDetails?.get('broker');
  }

  get customerDetailsCtrl() {
    return this.frmCompanyDetails?.get('customerDetails');
  }

  get sweetSpotCheckboxCtrl() {
    return this.frmCompanyDetails?.get('sweetSpotCheckbox');
  }

  get utilityDetailsCtrl() {
    return this.frmCompanyDetails?.get('utilityDetails');
  }

  get commodityCtrl() {
    return this.frmCompanyDetails?.get('commodityType');
  }

  get contractLengthsCtrl() {
    return this.frmCompanyDetails?.get('contractLengths');
  }

  get productTypeCtrl() {
    return this.frmCompanyDetails.get('productType');
  }

  get showBrokerageMaxLengthError(): boolean {
    return this.formErrors('brokerage', 'maxlength');
  }

  get showBrokerMaxLengthError(): boolean {
    return this.formErrors('broker', 'maxlength');
  }

  ngOnInit(): void {
    this.bindWizardSteps();
    this.dispatchActions();
    this.initialiseSubscriptionsAndSelectors();
    this.loadSupplierInformation();
    this.initialiseCommodity();
    this.initialiseCompanyDetailsForm();
    this.load();
  }

  ngOnDestroy(): void {
    this.selectLoggedInUserSubscription$?.unsubscribe();
    this.selectLoggedInUtilityHubIdentitySubscription$?.unsubscribe();
    this.quoteRequestStatus$?.unsubscribe();
    this.loadSubscription$?.unsubscribe();
    this.saveCompanyDetails$?.unsubscribe();
    this.utilities$?.unsubscribe();
    this.saveCustomCompanyDetails$?.unsubscribe();
    this.routeSubscription$?.unsubscribe();
  }

  bindWizardSteps() {
    this.wizardService.bindStep(this.route, () => from(this.save()));
  }

  dispatchActions() {
    this.store.dispatch(new SetQuoteRequestType(this.renewalsCustomerDetailsData.quoteRequestId, QuoteRequestType.Custom));
    this.store.dispatch(new SetCustomerType(this.renewalsCustomerDetailsData.quoteRequestId, CustomerType.Commercial));
    this.store.dispatch(new SetPageName(this.renewalsCustomerDetailsData.quoteRequestId, 'companydetails'));
    this.store.dispatch(new SetSalesType(this.renewalsCustomerDetailsData.quoteRequestId, SalesType.renewal));
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.renewalsCustomerDetailsData.quoteRequestId, true));
    this.store.dispatch(new SetQuoteRequestType(this.renewalsCustomerDetailsData.quoteRequestId, QuoteRequestType.Custom));
    this.store.dispatch(new SetCustomerType(this.renewalsCustomerDetailsData.quoteRequestId, CustomerType.Commercial));
    this.store.dispatch(new SetPageName(this.renewalsCustomerDetailsData.quoteRequestId, 'companydetails'));
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.renewalsCustomerDetailsData.quoteRequestId, true));
  }

  initialiseSubscriptionsAndSelectors() {
    this.isDynamic$ = this.store.select(isDynamicSelector);
    this.brokerageName$ = this.store.select(brokerageNameSelector);
    this.zones$ = this.store.select(zonesSelector);

    this.originalQuoteRequestIdSelector$ = this.store.select(originalQuoteRequestIdSelector).subscribe((originalQuoteRequestId) => {
      this.renewalsCustomerDetailsData.originalQuoteRequestId = originalQuoteRequestId;
    });

    this.selectLoggedInUserSubscription$ = this.store.select(loggedInUserSelector).subscribe((user) => {
      const userIsValid = user && !user.expired;
      if (userIsValid) {
        this.selectLoggedInUtilityHubIdentitySubscription$ = this.store
          .select(loggedInUtilityHubIdentitySelector)
          .subscribe((userIdentity) => {
            this.renewalsCustomerDetailsData.userIdentity = userIdentity;
          });
      }
    });

    this.store.select(configurationSelector).subscribe((config) => {
      this.renewalsCustomerDetailsData.utilityHubConfiguration = config;
      this.renewalsCustomerDetailsData.dunsEnabled = config.supplierConfiguration.dunsLookupEnabled;
    });

    this.quoteRequestStatus$ = this.store.select(quoteRequestStatusSelector).subscribe((x) => {
      this.renewalsCustomerDetailsData.isRequestedStatus = x !== QuoteRequestStatus.InProgress;
    });

    this.productTypes$ = this.store.select(productTypesFromConfigSelector).pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev?.map((x) => x)) === JSON.stringify(curr?.map((y) => y))),
      switchMap((products) => {
        const singleProduct = products?.length === 1;
        if (singleProduct) {
          this.selectSingleProduct(products[0]);
          return of(products);
        }
        this.productTypeCtrl.enable();
        return of(products);
      })
    );
  }

  loadSupplierInformation() {
    this.renewalsCustomerDetailsData.externalId = this.utilityHubService.getExternalId(this.renewalsCustomerDetailsData.userIdentity);
    this.utilityHubService.getSupplierUtilityInformation(this.renewalsCustomerDetailsData.externalId).subscribe((resp) => {
      this.store.dispatch(new SupplierUtilityInformation(resp));
    });
  }

  initialiseCommodity() {
    forkJoin({
      commodityType: this.store.select(commodityTypeSelector).pipe(take(1)),
      allowCustomElectric: this.store.select(isCustomJourneyElectricEnabledSelector).pipe(take(1)),
      allowCustomGas: this.store.select(isCustomJourneyGasEnabledSelector).pipe(take(1)),
      allowCustom: this.store.select(isCustomJourneyEnabledSelector).pipe(take(1)),
      isDynamic: this.isDynamic$.pipe(take(1)),
      routeParams: this.route.params.pipe(take(1)),
    })
      .pipe(
        take(1),
        tap((result) => {
          const quoteRequestId = result.routeParams['quoteRequestId'];
          const userIsValid =
            UserRole[this.renewalsCustomerDetailsData.userIdentity.currentOrganisation.role] === UserRole.SupplierManager &&
            result.isDynamic === true;
          const commodityTypeIsInformed = result.commodityType !== null && result.commodityType !== undefined;

          if (userIsValid && commodityTypeIsInformed) {
            const commodityType = result.commodityType;
            const isAllowed = commodityType === CommodityType.Gas ? result.allowCustomGas : result.allowCustomElectric;
            if (isAllowed) this.setCommodityType(CommodityType[commodityType], quoteRequestId);
          } else if (!commodityTypeIsInformed) {
            this.route.queryParams
              .subscribe(({ commodityType }) => {
                if (commodityType) this.setCommodityType(CommodityType[commodityType], quoteRequestId);
              })
              .unsubscribe();
          } else {
            this.renewalsCustomerDetailsData.isGasEnabled = result.allowCustomGas;
            this.renewalsCustomerDetailsData.isElectricEnabled = result.allowCustomElectric;
          }

          this.renewalsCustomerDetailsData.pricingSource = this.pricingSourceService.fetchPricingSource(
            QuoteRequestType.Custom,
            UserRole[this.renewalsCustomerDetailsData.userIdentity.currentOrganisation.role],
            result.isDynamic,
            CommodityType[this.renewalsCustomerDetailsData.commodity.toLowerCase()]
          );

          const cannotDisplayContractLengths =
            result.allowCustom === true &&
            UserRole[this.renewalsCustomerDetailsData.userIdentity.currentOrganisation.role] === UserRole.SupplierManager &&
            this.renewalsCustomerDetailsData.pricingSource === PricingSource.ManualEntry;

          if (cannotDisplayContractLengths) {
            this.renewalsCustomerDetailsData.showContractLengths = false;
          }
        }),
        catchError((err) => {
          return of(err);
        })
      )
      .subscribe();
  }

  load() {
    this.routeSubscription$ = this.route.params.subscribe((routeParams) => {
      const quoteRequestId = routeParams.quoteRequestId;
      const quoteRequestIsInformed = routeParams.quoteRequestId && !isNaN(quoteRequestId);
      this.renewalsCustomerDetailsData.quoteRequestId = quoteRequestId;

      if (!quoteRequestIsInformed) {
        return this.logger.error('Quote Request Id is not informed');
      }

      this.loadSubscription$ = forkJoin({
        state: this.quoteRequestService.getQuoteRequestState(quoteRequestId),
        formData: this.quoteService.getCompanyDetails(quoteRequestId),
      }).subscribe((res) => {
        res.formData.saleType = SaleType.Renewal;

        const { formData, state } = res;
        const { customerDetails, saleType, productType, utilityDetails } = formData || {};
        const { customerType } = customerDetails || {};
        const { uom, commodity, iso, zone, weatherZone, utilityShort } = utilityDetails || {};
        const dispatches = [
          { action: new SetCustomerType(quoteRequestId, customerType) },
          { action: new SetSalesType(quoteRequestId, saleType) },
          { action: new SetProductType(quoteRequestId, productType) },
          { action: new SetUtilityUOM(quoteRequestId, uom) },
          { action: new SetCommodityType(quoteRequestId, commodity) },
          { action: new SetOriginalQuoteRequestId(state.renewedFromQuoteId) },
        ];

        dispatches.forEach((dispatch) => this.store.dispatch(dispatch.action));

        Object.assign(this.renewalsCustomerDetailsData, {
          address: formData?.location,
          companyDetails: formData,
          commodity,
          emitEventOnValueChange: false,
          files: formData?.loaDocuments,
          isElectricChecked: commodity == CommodityType.Electricity,
          isGasChecked: commodity == CommodityType.Gas,
          iso,
          quoteRequestState: state,
          uom,
          utilities: [utilityShort],
          weatherZones: [weatherZone],
          zones: [zone],
          saleType: SaleType.Renewal,
        });
        
        this.sweetSpotCheckboxCtrl.enable();
        this.frmCompanyDetails.patchValue(this.renewalsCustomerDetailsData.companyDetails, { emitEvent: true });
        this.utilityDetailsCtrl.setValue(utilityDetails);
        this.commodityCtrl.setValue(commodity);
      });
    });

    this.route.queryParams.subscribe((x) => (this.renewalsCustomerDetailsData.isQuickSubmission = x.quickSubmission)).unsubscribe();
  }

  initialiseCompanyDetailsForm() {
    const brokerage = new FormControl('', Validators.maxLength(255));
    const broker = new FormControl({ value: '', disabled: true }, Validators.maxLength(255));
    const saleType = new FormControl({ value: SaleType.Renewal, disabled: true });
    const customerDetails = new FormControl('');
    const notes = new FormControl('', Validators.maxLength(255));
    const sweetSpotCheckbox = new FormControl({ value: true, disabled: false });
    const contractLengths = new FormControl({ value: '' });
    const productType = new FormControl('', Validators.required);
    const commodityType = new FormControl(
      this.renewalsCustomerDetailsData.isElectricChecked ? [CommodityType.Electricity] : [CommodityType.Gas]
    );
    const utilityDetails = new FormControl('');

    this.frmCompanyDetails = this.fb.group({
      brokerage,
      broker,
      customerDetails,
      notes,
      sweetSpotCheckbox,
      contractLengths,
      productType,
      commodityType,
      utilityDetails,
      saleType,
    });
  }

  isSupplierManagerManualEntry(): Observable<boolean> {
    return of(true);
  }

  resolveWaitForUploadConfirmation: () => Observable<boolean>;

  onDeleteUpload(eventData: any) {
    this.logger.info('onDeleteUpload', eventData);
  }

  onCompleteUpload(eventData: any) {
    this.logger.info('onCompleteUpload', eventData);
  }

  isQuoteRequestInvalidated() {
    return (
      this.frmCompanyDetails?.invalid &&
      this.renewalsCustomerDetailsData.isRequestedStatus &&
      this.renewalsCustomerDetailsData.quoteRequestId &&
      this.renewalsCustomerDetailsData.quoteRequestId !== 'new'
    );
  }

  formErrors(fieldName: string, errorType: string) {
    return this.frmCompanyDetails.get(fieldName)?.errors?.[errorType];
  }

  productSelectionChanged(productType: string) {
    this.store.dispatch(new SetProductType(this.renewalsCustomerDetailsData.quoteRequestId, productType));
  }

  selectSingleProduct(productType: ProductTypeConfiguration) {
    if (productType) {
      this.productTypeCtrl.setValue(productType.productType);
      this.productTypeCtrl.disable();
      this.productSelectionChanged(productType.productType);
    }
  }

  storeUtilityInformation(address: Address) {
    this.renewalsCustomerDetailsData.address = address;
    this.store.dispatch(new ClearUtility(this.renewalsCustomerDetailsData.quoteRequestId));
    this.store.dispatch(new ClearUtilityInformation(this.renewalsCustomerDetailsData.quoteRequestId));
    this.store.dispatch(
      new SetUtilityInformation(
        this.renewalsCustomerDetailsData.quoteRequestId,
        address.state,
        QuoteRequestType.Custom,
        address.zipCode,
        this.commodityCtrl.value
      )
    );
    this.store.dispatch(new SetUtility(this.renewalsCustomerDetailsData.quoteRequestId, this.renewalsCustomerDetailsData.utilities[0]));
  }

  brokerageChange(brokerageName: string) {
    this.store.dispatch(new SetBrokerageName(this.renewalsCustomerDetailsData.quoteRequestId, brokerageName));
    this.brokerageName$.subscribe((brokerageName) => {
      if (brokerageName) this.brokerCtrl.enable();
    });
  }

  allContractLengthsSelected(selected: boolean) {
    this.frmCompanyDetails.get('sweetSpotCheckbox').patchValue(selected);
    this.frmCompanyDetails.get('sweetSpotCheckbox').enable();
  }

  sweetSpotCheckboxChanged(event: any) {
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.renewalsCustomerDetailsData.quoteRequestId, event.checked));
  }

  next() {
    this.renewalsCustomerDetailsData.showValidation = true;
    this.frmCompanyDetails.markAllAsTouched();
    this.wizardService.next(this.isQuoteRequestInvalidated());
  }

  commodityChanged(event$: any) {
    const commodityType = <CommodityType>event$.value;
    this.renewalsCustomerDetailsData.commodity = commodityType;
    this.renewalsCustomerDetailsData.showControls = { ...this.renewalsCustomerDetailsData.showControls };
    this.store.dispatch(new SetCommodityType(this.renewalsCustomerDetailsData.quoteRequestId, commodityType));
    if (this.renewalsCustomerDetailsData.address) {
      this.storeUtilityInformation(this.renewalsCustomerDetailsData.address);
    }
  }

  async save(): Promise<any> {
    if (this.frmCompanyDetails.disabled || (this.frmCompanyDetails.pristine && this.frmCompanyDetails.valid)) {
      return true;
    }

    if (this.frmCompanyDetails.valid) {
      const { customerDetail, quoteRequestId: id, defaultContractLengthButtonsToShow } = this.renewalsCustomerDetailsData;
      const quoteRequestId = isNaN(id) || !id ? 0 : id;
      const formRawValues = this.frmCompanyDetails.getRawValue();
      const customerDetails = this.constructDetails(
        formRawValues,
        quoteRequestId,
        defaultContractLengthButtonsToShow,
        customerDetail?.files
      );
      const quoteData = this.constructDetails(
        {
          ...formRawValues,
          sweetSpotCheckbox: this.sweetSpotCheckboxCtrl.value,
        },
        quoteRequestId,
        defaultContractLengthButtonsToShow,
        customerDetail?.files
      );

      this.saveCompanyDetails$ = this.quoteService.saveCompanyDetails(quoteRequestId, customerDetails).subscribe((savedQuoteRequestId) => {
        quoteData.quoteRequestId = savedQuoteRequestId;

        this.quoteService.saveQuoteConfiguration(savedQuoteRequestId, quoteData).subscribe(() => {
          this.store.dispatch(new SetContractLengths(id, quoteData.contractLengths));
          LogRocket.track('new_custom_quote');
          this.navigateAndBind(quoteData);
        });
      });
    }
  }

  utilityUomSelected($event) {
    this.renewalsCustomerDetailsData.uom = $event;
  }

  utilitySelected(utility: string) {
    this.zones$.subscribe((x) => (this.renewalsCustomerDetailsData.zones = x));
    this.renewalsCustomerDetailsData.weatherZones = {
      ...this.renewalsCustomerDetailsData.utilityZones.find((uz) => uz.utilityName === utility),
    }.weatherZones;
  }

  private constructDetails = (rawValues, quoteRequestId, defaultContractLengthButtonsToShow, files) => {
    let { contractLengths } = rawValues;
    contractLengths = removeDuplicatePrimitives(contractLengths ?? defaultContractLengthButtonsToShow);

    return { ...rawValues, contractLengths, loaDocuments: files, quoteRequestId };
  };

  private navigateAndBind = (quoteData: any) => {
    this.router
      .navigate(['renewals-custom', quoteData.quoteRequestId, RenewalsCustomConstants.wizardSteps[0].slug], {
        queryParams: { commodityType: quoteData.utilityDetails.commodity },
      })
      .then(() => {
        this.route.params.subscribe(() => {
          this.wizardService.bindStep(this.route, () => of(true));
          this.wizardService.next();
        });
      });
  };

  private setCommodityType = (type: CommodityType, quoteRequestId: string) => {
    this.renewalsCustomerDetailsData[`is${type}Enabled`] = true;
    this.renewalsCustomerDetailsData[`is${type}Checked`] = true;
    this.renewalsCustomerDetailsData.commodity = type;
    this.store.dispatch(new SetCommodityType(quoteRequestId, type));
  };
}
